import { fetchAuthSession } from '@aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  details?: string[];
}

class ApiService {
  private static async getAuthToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }

  private static async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const token = await this.getAuthToken();

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Safely merge headers from options
      if (options.headers) {
        if (options.headers instanceof Headers) {
          options.headers.forEach((value, key) => {
            headers[key] = value;
          });
        } else if (Array.isArray(options.headers)) {
          options.headers.forEach(([key, value]) => {
            headers[key] = value;
          });
        } else {
          Object.assign(headers, options.headers);
        }
      }

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Request failed');
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // User Profile API
  static async getUserProfile() {
    return this.makeRequest<any>('/users/profile');
  }

  static async updateUserProfile(profileData: any) {
    return this.makeRequest<any>('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  // Quotes API
  static async getUserQuotes() {
    return this.makeRequest<any[]>('/quotes');
  }

  static async getQuoteById(quoteId: string) {
    return this.makeRequest<any>(`/quotes/${quoteId}`);
  }

  static async createQuote(quoteData: any) {
    return this.makeRequest<any>('/quotes', {
      method: 'POST',
      body: JSON.stringify(quoteData),
    });
  }

  static async updateQuote(quoteId: string, updates: any) {
    return this.makeRequest<any>(`/quotes/${quoteId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  static async deleteQuote(quoteId: string) {
    return this.makeRequest<any>(`/quotes/${quoteId}`, {
      method: 'DELETE',
    });
  }

  static async recalculatePremium(quoteId: string) {
    return this.makeRequest<any>(`/quotes/${quoteId}/recalculate`, {
      method: 'POST',
    });
  }

  // Claims API
  static async getUserClaims() {
    return this.makeRequest<any[]>('/claims');
  }

  static async getClaimById(claimId: string) {
    return this.makeRequest<any>(`/claims/${claimId}`);
  }

  static async createClaim(claimData: any) {
    return this.makeRequest<any>('/claims', {
      method: 'POST',
      body: JSON.stringify(claimData),
    });
  }

  static async updateClaim(claimId: string, updates: any) {
    return this.makeRequest<any>(`/claims/${claimId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  static async deleteClaim(claimId: string) {
    return this.makeRequest<any>(`/claims/${claimId}`, {
      method: 'DELETE',
    });
  }

  static async addClaimNote(claimId: string, note: string) {
    return this.makeRequest<any>(`/claims/${claimId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }
}

export default ApiService;